import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronRight, ChevronLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICES } from "@/lib/constants";

const CONTACT_METHODS = ["Phone", "SMS", "Email"];

const EMPTY = {
  service: "",
  vehicle: "",
  name: "",
  email: "",
  phone: "",
  preferred_contact: "Phone",
  notes: "",
};

const inputCls =
  "w-full bg-surface border border-surface-2 px-4 py-3 text-white placeholder:text-zinc-600 font-sans text-base focus:outline-none focus:border-hazard focus:ring-1 focus:ring-hazard transition-colors";

export const BookingSection = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const step1Valid = form.service;
  const step2Valid = form.vehicle.trim();
  const step3Valid = form.name.trim() && form.email.trim() && form.phone.trim();

  const submit = async () => {
    if (!step3Valid) return;
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Booking request sent!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setStep(1);
    setDone(false);
  };

  return (
    <section id="book" data-testid="booking" className="relative bg-surface py-20 md:py-32 border-t border-surface-2">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20">
        <div>
          <h2 className="font-display uppercase leading-[0.85] text-5xl md:text-7xl lg:text-8xl mb-6">
            Book <br /> a Job
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md mb-10">
            Tell us what you need. We'll confirm your booking and get in touch to lock in
            a time — you'll also get a confirmation by email.
          </p>

          <div className="space-y-1">
            {["Service", "Vehicle", "Your Details"].map((label, i) => {
              const n = i + 1;
              const state = done ? "done" : step === n ? "active" : step > n ? "done" : "idle";
              return (
                <div key={label} className="flex items-center gap-4 py-3 border-b border-surface-2">
                  <span
                    className={`w-8 h-8 flex items-center justify-center font-mono text-sm ${
                      state === "active"
                        ? "bg-hazard text-black"
                        : state === "done"
                        ? "bg-white text-black"
                        : "bg-surface-2 text-zinc-500"
                    }`}
                  >
                    {state === "done" ? <Check size={16} /> : `0${n}`}
                  </span>
                  <span
                    className={`font-mono text-xs tracking-[0.2em] uppercase ${
                      state === "idle" ? "text-zinc-600" : "text-white"
                    }`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-ink border border-surface-2 p-6 md:p-10 min-h-[440px] flex flex-col">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                data-testid="booking-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center text-center"
              >
                <CheckCircle2 size={64} className="text-hazard mb-6" />
                <h3 className="font-display uppercase text-4xl md:text-5xl mb-3">Booking Sent</h3>
                <p className="text-zinc-400 max-w-sm mb-8">
                  Cheers {form.name.split(" ")[0]}! We've received your request for{" "}
                  <span className="text-white">{form.service}</span>. Check your inbox for a
                  confirmation — we'll be in touch to lock it in.
                </p>
                <button
                  onClick={reset}
                  data-testid="booking-reset-btn"
                  className="bg-hazard text-black px-8 py-3 font-mono text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors"
                >
                  Make Another Booking
                </button>
              </motion.div>
            ) : (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
                className="flex-1 flex flex-col"
              >
                {step === 1 && (
                  <div className="flex-1">
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-6">
                      Select a service
                    </p>
                    <div className="grid gap-3">
                      {SERVICES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`booking-service-${s.id}`}
                          onClick={() => {
                            set("service", s.slug);
                            setStep(2);
                          }}
                          className={`flex items-center justify-between px-5 py-4 border text-left transition-colors ${
                            form.service === s.slug
                              ? "border-hazard bg-hazard/10"
                              : "border-surface-2 hover:border-zinc-600"
                          }`}
                        >
                          <span className="font-display uppercase text-xl md:text-2xl tracking-wide">
                            {s.title}
                          </span>
                          {form.service === s.slug && <Check size={20} className="text-hazard" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="flex-1 space-y-6">
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">
                      Tell us about the vehicle
                    </p>
                    <div>
                      <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                        Vehicle (Make / Model / Year)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Toyota Hilux 2021"
                        data-testid="booking-vehicle"
                        value={form.vehicle}
                        onChange={(e) => set("vehicle", e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <p className="text-zinc-500 text-sm leading-relaxed">
                      No need to pick a date or time — once we receive your request we'll get in
                      touch to lock in a time that suits you.
                    </p>
                  </div>
                )}

                {step === 3 && (
                  <div className="flex-1 space-y-5">
                    <div>
                      <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        placeholder="Your name"
                        data-testid="booking-name"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="you@email.com"
                          data-testid="booking-email"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          placeholder="04XX XXX XXX"
                          data-testid="booking-phone"
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                        Preferred Contact Method
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {CONTACT_METHODS.map((m) => (
                          <button
                            key={m}
                            type="button"
                            data-testid={`booking-contact-${m.toLowerCase()}`}
                            onClick={() => set("preferred_contact", m)}
                            className={`px-3 py-2.5 border font-mono text-xs tracking-[0.1em] uppercase transition-colors ${
                              form.preferred_contact === m
                                ? "border-hazard bg-hazard/10 text-white"
                                : "border-surface-2 text-zinc-400 hover:border-zinc-600"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">
                        Notes (optional)
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Anything we should know?"
                        data-testid="booking-notes"
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        className={`${inputCls} resize-none`}
                      />
                    </div>
                  </div>
                )}

                {/* nav */}
                <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-surface-2">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      data-testid="booking-back-btn"
                      className="flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors"
                    >
                      <ChevronLeft size={16} /> Back
                    </button>
                  ) : (
                    <span />
                  )}

                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={() => setStep(step + 1)}
                      disabled={step === 1 ? !step1Valid : !step2Valid}
                      data-testid="booking-next-btn"
                      className="flex items-center gap-2 bg-hazard text-black px-7 py-3 font-mono text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Continue <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={!step3Valid || submitting}
                      data-testid="booking-submit-btn"
                      className="flex items-center gap-2 bg-hazard text-black px-7 py-3 font-mono text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          Confirm Booking <Check size={16} />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
